import { FC, useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, Typography } from '@mui/material';
import { AxiosPromise } from 'axios';
import { useFeedback } from 'mir-utils/providers';
import { notNull } from 'mir-utils/utils/utilities';
import { useTranslation } from '../i18n';
import { Loading } from './Loading';

type inlineFunctionReturn = AxiosPromise<any> | Promise<any>;

type ActionType = {
  actionMessage: string;
  inlineFunction: () => inlineFunctionReturn;
};

export interface Props {
  children: (initAction: ({ actionMessage, inlineFunction }: ActionType) => void) => JSX.Element;
  cancelFunction?: () => void;
  initialState?: 'idle' | 'question' | 'loading';
  noFeedback?: boolean;
}

/**
 * @summary Компонент, который позволяет подтверждать inline-действия в таблице.
 *
 * @param children - Функция-ребёнок, аргумент которой - функция инициализации действия.
 * @param noFeedback
 * @param cancelFunction
 * @param initialState
 *
 * @description Аргумент в child-функции нужно применить тогда, когда необходимо совершить действие.
 * К примеру, её можно назначить, как onClick на кнопку удаления. После того, как пользователь
 * нажмёт на кнопку, появится подтверждение действия, а после подтверждения сработает функция
 * inlineFunction. Во время её работы, всё содержимое ячейки управления будет заменено крутилкой.
 */
export const MultipleInlineAction: FC<Props> = ({
  children,
  noFeedback,
  cancelFunction,
  initialState = 'idle',
}) => {
  const { t } = useTranslation();
  const { showFeedback } = useFeedback();

  const [status, setStatus] = useState<'idle' | 'question' | 'loading' | 'error'>(initialState);
  const [error, setError] = useState<unknown | null>(null);

  const [action, setAction] = useState<ActionType | null>(null);

  const handleAction = async () => {
    if (action !== null) {
      setStatus('loading');
      try {
        await action.inlineFunction();
        !noFeedback &&
          showFeedback({
            message: t('Changes saved'),
            severity: 'success',
          });
        setStatus('idle');
      } catch (error) {
        setError(error);
        setStatus('error');
      }
    }
  };

  switch (status) {
    case 'question':
      return (
        <Box display="flex" alignItems="center" justifyContent="center">
          <Typography variant="subtitle2">{notNull(action).actionMessage}</Typography>
          <IconButton size="small" onClick={handleAction} data-test="InlineActionAgree">
            <CheckIcon color="success" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => (cancelFunction === undefined ? setStatus('idle') : cancelFunction())}
            data-test="InlineActionDisagree"
          >
            <CloseIcon color="error" />
          </IconButton>
        </Box>
      );
    case 'loading':
      return <Loading progressSize="small" />;
    case 'error':
      throw error;
    default:
      return (
        <>
          {children(({ actionMessage, inlineFunction }) => {
            setStatus('question');
            setAction({
              actionMessage,
              inlineFunction,
            });
          })}
        </>
      );
  }
};
